import { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';

const BASE_URL = '/api';

async function getMovements(offerId) {
  const res = await fetch(`${BASE_URL}/products/${encodeURIComponent(offerId)}/movements`);
  return res.json();
}

async function saveBarcode(offerId, barcode) {
  const token = localStorage.getItem('ozon_token');
  const res = await fetch(`${BASE_URL}/products/${encodeURIComponent(offerId)}/barcode`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ barcode }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Hata');
  return data;
}

// ── Barkod düzenleme modalı ───────────────────────────────────────────────────
function BarcodeModal({ product, onClose, onSaved }) {
  const [value, setValue] = useState(product.barcode || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    const trimmed = value.trim();
    if (!trimmed) { setError('Barkod boş bırakılamaz'); return; }
    setSaving(true);
    setError('');
    try {
      const updated = await saveBarcode(product.offer_id, trimmed);
      onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!product.barcode) { onClose(); return; }
    if (!window.confirm('Barkod atamasını kaldırmak istiyor musunuz?')) return;
    setSaving(true);
    try {
      // boş string ile güncelle → backend reject eder, o yüzden direkt SQL yapmak yerine
      // offer_id ye NULL atalım — şimdilik boş string gönderirsek backend hata verir
      // bunun için ayrı bir clear endpoint gerekli, şimdilik uyarı göster
      alert('Barkod temizleme için şu an manuel DB güncellemesi gerekiyor. Yakında eklenecek.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          {/* Başlık */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Barkod Ata</h2>
              <p className="text-sm text-gray-500 truncate max-w-xs mt-0.5">{product.name}</p>
              <p className="text-xs text-gray-400 font-mono">{product.offer_id}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none mt-0.5">✕</button>
          </div>

          {/* Bilgi */}
          <div className="bg-indigo-50 rounded-xl p-3 mb-4 text-xs text-indigo-700">
            <strong>📱 Nasıl çalışır?</strong> Burada girdiğin barkod, mobilden ürün taranınca otomatik eşleştirilir.
            Mobil uygulamada tarayarak da eşleştirme yapabilirsin.
          </div>

          {/* Input */}
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5 tracking-wide">
            Fiziksel Barkod (EAN-13, Code128 vb.)
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(''); }}
            placeholder="ör: 3264680025500"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-300"
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            autoFocus
          />
          {error && <p className="text-xs text-red-500 mt-1.5">{error}</p>}

          {/* Mevcut barkod göster */}
          {product.barcode && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-400">Mevcut:</span>
              <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{product.barcode}</span>
            </div>
          )}
        </div>

        {/* Butonlar */}
        <div className="border-t border-gray-100 px-6 py-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-600 transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-xl text-sm font-semibold text-white transition-colors"
          >
            {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StockBadge({ quantity, reserved }) {
  const available = quantity - (reserved || 0);
  if (quantity === 0)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Stok Yok</span>;
  if (available <= 0)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Rezerve: {reserved}</span>;
  if (quantity < 5)
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Düşük: {quantity}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">{quantity} adet</span>;
}

function MovementsModal({ product, onClose }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMovements(product.offer_id)
      .then(setMovements)
      .finally(() => setLoading(false));
  }, [product.offer_id]);

  const typeLabel = { in: 'Giriş', out: 'Çıkış', adjustment: 'Düzeltme' };
  const typeColor = { in: 'text-green-600', out: 'text-red-600', adjustment: 'text-blue-600' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Stok Hareketleri</h3>
            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{product.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !movements.length ? (
            <div className="text-center py-10 text-gray-400 text-sm">Hareket kaydı yok</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Tarih</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Tür</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500">Miktar</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Kaynak</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {movements.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-2.5 text-gray-400 text-xs whitespace-nowrap">
                      {new Date(m.created_at).toLocaleString('tr-TR')}
                    </td>
                    <td className={`px-4 py-2.5 font-medium ${typeColor[m.type]}`}>
                      {typeLabel[m.type]}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-semibold ${typeColor[m.type]}`}>
                      {m.type === 'out' ? '-' : '+'}{m.quantity}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      {m.reference ? (
                        <span className="font-mono">{m.reference}</span>
                      ) : (
                        m.note || m.reason
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StockEditModal({ product, onClose, onSaved }) {
  const [value, setValue] = useState(String(product.stock_quantity));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const diff = parseInt(value, 10) - product.stock_quantity;

  async function handleSave() {
    const qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) { setError('Geçerli bir sayı girin'); return; }
    setSaving(true);
    try {
      const updated = await api.updateStock(product.offer_id, qty, note);
      onSaved(updated);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Stok Güncelle</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <p className="text-sm text-gray-500 truncate">{product.name}</p>
            <p className="text-xs text-gray-400 mt-0.5 font-mono">{product.offer_id}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Yeni Stok Miktarı</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setValue((v) => String(Math.max(0, parseInt(v || '0', 10) - 1)))}
                className="w-9 h-9 rounded-lg border border-gray-200 text-lg font-medium hover:bg-gray-50 flex items-center justify-center"
              >−</button>
              <input
                type="number"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300"
                autoFocus
              />
              <button
                onClick={() => setValue((v) => String(parseInt(v || '0', 10) + 1))}
                className="w-9 h-9 rounded-lg border border-gray-200 text-lg font-medium hover:bg-gray-50 flex items-center justify-center"
              >+</button>
            </div>
            {!isNaN(diff) && diff !== 0 && (
              <p className={`text-xs mt-1.5 font-medium ${diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {diff > 0 ? `+${diff}` : diff} ({product.stock_quantity} → {value})
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Not (opsiyonel)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Örn: Yeni kargo geldi"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-6 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={saving || isNaN(parseInt(value, 10))}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg py-2.5 text-sm font-medium"
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | low | out
  const [editProduct, setEditProduct] = useState(null);
  const [movProduct, setMovProduct] = useState(null);
  const [barcodeProduct, setBarcodeProduct] = useState(null);
  const [syncingImages, setSyncingImages] = useState(false);

  const load = useCallback(() => {
    api.getProducts()
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleSaved(updated) {
    setProducts((prev) =>
      prev.map((p) => (p.offer_id === updated.offer_id ? { ...p, ...updated } : p))
    );
  }

  async function handleSyncImages() {
    setSyncingImages(true);
    try {
      const token = localStorage.getItem('ozon_token');
      const res = await fetch(`${BASE_URL}/products/sync-images?force=1`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      console.log(`[Görsel Sync] ${data.updated} görsel güncellendi`);
      await load();
    } catch (e) {
      console.error('[Görsel Sync Hata]', e.message);
      alert('Görsel sync hatası: ' + e.message);
    } finally {
      setSyncingImages(false);
    }
  }

  const filtered = products.filter((p) => {
    if (filter === 'low' && p.stock_quantity >= 5) return false;
    if (filter === 'out' && p.stock_quantity > 0) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.offer_id || '').toLowerCase().includes(q) ||
        String(p.sku || '').includes(q) ||
        (p.barcode || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const outCount = products.filter((p) => p.stock_quantity === 0).length;
  const lowCount = products.filter((p) => p.stock_quantity > 0 && p.stock_quantity < 5).length;

  if (loading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ürünler</h1>
          <p className="text-sm text-gray-400 mt-0.5">{products.length} ürün</p>
        </div>
        <button
          onClick={handleSyncImages}
          disabled={syncingImages}
          className="flex items-center gap-2 text-sm border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-60 px-4 py-2 rounded-lg font-medium"
        >
          {syncingImages ? <Spinner size="sm" /> : '🖼️'}
          Görselleri Güncelle
        </button>
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-400 uppercase tracking-wide">Toplam Ürün</div>
          <div className="text-3xl font-bold text-gray-800 mt-1">{products.length}</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 cursor-pointer" onClick={() => setFilter(filter === 'low' ? 'all' : 'low')}>
          <div className="text-xs text-yellow-600 uppercase tracking-wide">Düşük Stok (&lt;5)</div>
          <div className="text-3xl font-bold text-yellow-700 mt-1">{lowCount}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 cursor-pointer" onClick={() => setFilter(filter === 'out' ? 'all' : 'out')}>
          <div className="text-xs text-red-600 uppercase tracking-wide">Stok Yok</div>
          <div className="text-3xl font-bold text-red-700 mt-1">{outCount}</div>
        </div>
      </div>

      {/* Arama + filtre */}
      <div className="flex gap-3 mb-4">
        <input
          type="text"
          placeholder="Ürün adı, offer_id veya SKU ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-sm border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'low', label: 'Düşük' },
            { key: 'out', label: 'Tükenen' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ürün tablosu */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase w-14"></th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ürün</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">SKU</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Barkod</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Rezerve</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Stok</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-500 text-xs uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((product) => (
              <tr key={product.offer_id} className="hover:bg-gray-50 transition-colors">
                {/* Görsel */}
                <td className="px-4 py-3">
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover border border-gray-100"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 text-lg">
                      📦
                    </div>
                  )}
                </td>

                {/* İsim + Offer ID */}
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800 max-w-xs truncate">{product.name || '—'}</div>
                  <div className="text-xs text-gray-400 font-mono mt-0.5">{product.offer_id}</div>
                </td>

                {/* SKU */}
                <td className="px-4 py-3 font-mono text-xs text-gray-400">
                  {product.sku || '—'}
                </td>

                {/* Barkod */}
                <td className="px-4 py-3">
                  {product.barcode ? (
                    <button
                      onClick={() => setBarcodeProduct(product)}
                      className="flex items-center gap-1.5 group"
                      title="Barkodu düzenle"
                    >
                      <span className="font-mono text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded group-hover:bg-indigo-100 transition-colors">
                        {product.barcode}
                      </span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setBarcodeProduct(product)}
                      className="text-xs text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 px-2 py-0.5 rounded transition-colors"
                      title="Barkod ata"
                    >
                      + Ata
                    </button>
                  )}
                </td>

                {/* Rezerve (aktif siparişlerdeki) */}
                <td className="px-4 py-3 text-center">
                  {product.reserved_quantity > 0 ? (
                    <span className="text-orange-600 font-semibold">{product.reserved_quantity}</span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>

                {/* Stok */}
                <td className="px-4 py-3 text-center">
                  <StockBadge quantity={product.stock_quantity} reserved={product.reserved_quantity} />
                </td>

                {/* İşlem butonları */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setEditProduct(product)}
                      className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-medium transition-colors"
                    >
                      Stok Gir
                    </button>
                    <button
                      onClick={() => setMovProduct(product)}
                      className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-lg text-xs font-medium transition-colors"
                    >
                      Geçmiş
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={7} className="text-center py-14 text-gray-400">
                  Ürün bulunamadı.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modaller */}
      {editProduct && (
        <StockEditModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSaved={handleSaved}
        />
      )}
      {movProduct && (
        <MovementsModal
          product={movProduct}
          onClose={() => setMovProduct(null)}
        />
      )}
      {barcodeProduct && (
        <BarcodeModal
          product={barcodeProduct}
          onClose={() => setBarcodeProduct(null)}
          onSaved={(updated) => {
            handleSaved(updated);
            setBarcodeProduct(null);
          }}
        />
      )}
    </div>
  );
}
